/**
 * Politique Trusted Types (navigateur) pour CSP require-trusted-types-for 'script'.
 * Doit être chargée avant tout autre module qui touche au DOM (router → seoService).
 *
 * En production, le build injecte aussi un script équivalent en début de <body>
 * (vite-plugin-csp-nonce) : createPolicy en double est ignorée (try/catch).
 */

declare global {
  interface Window {
    trustedTypes?: TrustedTypePolicyFactory & {
      defaultPolicy?: TrustedTypePolicy | null
    }
  }
}

const ALLOWED_SCRIPT_URL_PREFIXES = [
  'https://js.stripe.com',
  'https://www.googletagmanager.com',
  'https://accounts.google.com',
  'https://www.paypal.com',
  'https://www.paypalobjects.com',
  'https://www.google-analytics.com',
  'https://connect.facebook.net',
  'https://www.google.com',
] as const

export function setupTrustedTypes(): void {
  if (typeof window === 'undefined' || !window.trustedTypes?.createPolicy) {
    return
  }
  try {
    window.trustedTypes.createPolicy('default', {
      createScript(input: string): string {
        try {
          JSON.parse(input)
          return input
        } catch {
          throw new TypeError(
            `Trusted Types: script.text bloqué — contenu non-JSON : ${input.substring(0, 80)}`,
          )
        }
      },
      createScriptURL(input: string): string {
        if (typeof input !== 'string') {
          throw new TypeError('Trusted Types: createScriptURL attend une string')
        }
        if (input.startsWith('/') || input.startsWith('./') || input.startsWith('../')) {
          return input
        }
        let url: URL
        try {
          url = new URL(input)
        } catch {
          throw new TypeError(`Trusted Types: URL invalide : ${input}`)
        }
        if (url.origin === window.location.origin) {
          return input
        }
        const allowed = ALLOWED_SCRIPT_URL_PREFIXES.some(
          (origin) => input === origin || input.startsWith(`${origin}/`),
        )
        if (!allowed) {
          throw new TypeError(`Trusted Types: script.src bloqué — origine non autorisée : ${url.origin}`)
        }
        return input
      },
      createHTML(input: string): string {
        return input
      },
    })
  } catch {
    // Politique déjà enregistrée (script inline du build ou double chargement)
  }
}

setupTrustedTypes()
